import {
    AlertRegular,
    AlertOffRegular,
    ArrowSyncRegular,
    PremiumRegular,
    SettingsRegular,
} from '@fluentui/react-icons';
import { CalendarTodoRegular, TeamsRegular } from '@fluentui/react-brand-icons';
import getGlobalSettingsAccountMailboxInfo from 'owa-account-source-list-store/lib/selectors/getGlobalSettingsAccountMailboxInfo';
import { getUnseenItemsCount as getUnseenNotificationsCount } from 'owa-activity-feed/lib/store/unseenItemsSelector';
import { logUsage } from 'owa-analytics';
import getDrawerPaneId from 'owa-app-pane-group-utils/lib/getDrawerPaneId';
import { isFeatureEnabled } from 'owa-feature-flags';
import loc from 'owa-localize';
import { lazyManualSyncMail } from 'owa-mail-manual-sync';
import getSelectedTableView from 'owa-mail-list-store/lib/utils/getSelectedTableView';
import areNotificationsDisabled from 'owa-notifications-settings/lib/selectors/areNotificationsDisabled';
import { lazyMountAndShowFullOptions } from 'owa-options-view';
import { isPremiumConsumer } from 'owa-session-store';
import { getUnreadConversationsCount as getTeamsUnreadConversationsCount } from 'owa-teams-embed-client-store';
import { isTimePanelAvailable } from 'owa-time-panel-bootstrap';
import { lazyOpenPremiumDashboard } from 'owa-whats-new';
import {
    MY_DAY_BUTTON_ID,
    NOTIFICATIONS_BUTTON_ID,
    PREMIUM_BUTTON_ID,
    SETTINGS_BUTTON_ID,
    TEAMS_BUTTON_ID,
    SYNC_BUTTON_ID,
} from '../../constants/constants';
import type { OwaHeaderButtonControl } from '../../types/OwaHeaderButtonControl';
import onMyDayButtonClicked from '../../utils/buttons/onMyDayButtonClicked';
import isTeamsChatButtonEnabled from '../../utils/buttons/isTeamsChatButtonEnabled';
import MyDayPane from '../panes/MyDayPane';
import NotificationsPane from '../panes/NotificationsPane';
import TeamsChatPane from '../panes/TeamsChatPane';
import {
    myDayHeaderButton,
    notificationsDisabledHeaderButton,
    notificationsHeaderButton,
    notificationsHeaderButtonWithCount,
    premiumHeaderButton,
    settingsHeaderButton,
    syncHeaderButton,
    syncHeaderButtonTooltip,
    teamsChatHeaderButton,
    teamsChatHeaderButtonWithCount,
} from './OwaHeaderButton.locstring.json';
import { brandIcon } from './OwaHeaderButtonGroup.scss';

/*
 * The order of the buttons in this array is important.
 * The buttons are rendered in the order they are defined here from left to right.
 */
export const headerButtons: readonly OwaHeaderButtonControl[] = [
    {
        id: PREMIUM_BUTTON_ID,
        renderLabel: () => loc(premiumHeaderButton),
        renderIcon: () => <PremiumRegular />,
        onClick: () => {
            logUsage('HeaderPremiumDiamondClicked', getGlobalSettingsAccountMailboxInfo());
            lazyOpenPremiumDashboard.importAndExecute();
        },
        isVisible: () => isPremiumConsumer(getGlobalSettingsAccountMailboxInfo()),
        overflowItem: {
            priority: 2,
            divider: 'after',
        },
    },
    {
        id: TEAMS_BUTTON_ID,
        renderLabel: () => loc(teamsChatHeaderButton),
        renderIcon: () => <TeamsRegular className={brandIcon} />,
        isVisible: () => isTeamsChatButtonEnabled(),
        pane: {
            id: getDrawerPaneId('teamsChat'),
            render: id => <TeamsChatPane paneId={id} />,
        },
        badge: {
            getCount: () => {
                return getTeamsUnreadConversationsCount();
            },
        },
        renderTooltip: () => {
            const unreadCount = getTeamsUnreadConversationsCount();
            return unreadCount > 0
                ? loc(teamsChatHeaderButtonWithCount, unreadCount)
                : loc(teamsChatHeaderButton);
        },
        overflowItem: {
            priority: 1,
        },
    },
    {
        id: MY_DAY_BUTTON_ID,
        renderLabel: () => loc(myDayHeaderButton),
        renderIcon: () => <CalendarTodoRegular />,
        onClick: () => onMyDayButtonClicked(),
        isVisible: () => isTimePanelAvailable(),
        pane: {
            id: getDrawerPaneId('myDay'),
            render: id => <MyDayPane paneId={id} />,
        },
        overflowItem: {
            priority: 3,
        },
    },
    {
        id: NOTIFICATIONS_BUTTON_ID,
        renderLabel: () => loc(notificationsHeaderButton),
        renderIcon: () =>
            // Show unmuted alert icon if notifications are enabled or are loading
            areNotificationsDisabled() ? <AlertOffRegular /> : <AlertRegular />,
        pane: {
            id: getDrawerPaneId('notifications'),
            render: id => <NotificationsPane paneId={id} />,
        },
        badge: {
            getCount: () => getUnseenNotificationsCount(),
        },
        renderTooltip: () => {
            if (areNotificationsDisabled()) {
                return loc(notificationsDisabledHeaderButton);
            }
            const unseenCount = getUnseenNotificationsCount();
            return unseenCount > 0
                ? loc(notificationsHeaderButtonWithCount, unseenCount)
                : loc(notificationsHeaderButton);
        },
        overflowItem: {
            priority: 5,
        },
    },
    {
        id: SYNC_BUTTON_ID,
        renderLabel: () => loc(syncHeaderButton),
        renderIcon: () => <ArrowSyncRegular />,
        onClick: () => {
            logUsage('SyncButtonClicked', getGlobalSettingsAccountMailboxInfo(), {
                source: 'Header',
            });
            lazyManualSyncMail.importAndExecute(getSelectedTableView());
        },
        isVisible: () => isFeatureEnabled('fwk-header-sync-button-and-cleanup'),
        renderTooltip: () => loc(syncHeaderButtonTooltip),
        overflowItem: {
            priority: 0,
        },
    },
    {
        id: SETTINGS_BUTTON_ID,
        renderLabel: () => loc(settingsHeaderButton),
        renderIcon: () => <SettingsRegular />,
        onClick: () => lazyMountAndShowFullOptions.importAndExecute(),
        overflowItem: {
            priority: 4,
            divider: 'before',
        },
    },
] as const;
